import {
  AlignItems,
  Body1,
  Box,
  Breakpoint,
  Button,
  ButtonColorVariant,
  Color,
  Display,
  Display1,
  DisplayPosition,
  FlexDirection,
  FontWeight,
  Icon,
  IconVariant,
  Text,
  withCitrusTheme,
  ZIndex,
} from '@gousto-internal/citrus-react'
import React from 'react'

import type { UnsubscribedHeroV2Data } from '@library/api-customer-engagement-experience'

import dishCollageDesktop from './assets/dish-collage-desktop.webp'
import dishCollageMobile from './assets/dish-collage-mobile.webp'
import dishCollageTablet from './assets/dish-collage-tablet.webp'

type EmptyStateHeroV2Props = {
  data: UnsubscribedHeroV2Data
  onActionClick: (intent: string) => void
}

const CARD_RADIUS = '8px'

const SCRIM_OPACITY = 0.9

const scrimBefore = (backgroundColor: string | undefined, inset: string | number) => ({
  content: '""',
  position: 'absolute' as const,
  inset,
  borderRadius: CARD_RADIUS,
  backgroundColor,
  opacity: SCRIM_OPACITY,
  zIndex: -1,
})

const Container = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: theme.color(Color.ColdGrey_800),
  borderRadius: CARD_RADIUS,
}))

const CollageImage = withCitrusTheme('img', () => ({
  position: 'absolute',
  top: 0,
  right: 0,
  height: '100%',
  width: 'auto',
  pointerEvents: 'none',
  userSelect: 'none',
}))

const Headline = withCitrusTheme(Display1, () => ({
  textTransform: 'uppercase',
  margin: 0,
}))

const Scrim = withCitrusTheme(Box, (theme) => ({
  position: 'relative',
  '&::before': scrimBefore(theme.color(Color.ColdGrey_800), '-2px -8px'),
}))

const ChecklistPill = withCitrusTheme('ul', (theme) => ({
  position: 'relative',
  listStyle: 'none',
  margin: 0,
  padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
  display: 'flex',
  flexDirection: 'column',
  gap: `${theme.spacing(1)}px`,
  '&::before': scrimBefore(theme.color(Color.ColdGrey_800), 0),
}))

const ChecklistItem = withCitrusTheme('li', (theme) => ({
  alignItems: 'center',
  display: 'flex',
  gap: `${theme.spacing(1)}px`,
  color: theme.color(Color.Success_50),
}))

export const EmptyStateHeroV2 = ({ data, onActionClick }: EmptyStateHeroV2Props) => (
  <Container data-testid="empty-state-hero-v2">
    <picture>
      <source media={`(min-width: ${Breakpoint.Large})`} srcSet={dishCollageDesktop} />
      <source media={`(min-width: ${Breakpoint.Medium})`} srcSet={dishCollageTablet} />
      <CollageImage alt="" aria-hidden="true" src={dishCollageMobile} />
    </picture>

    <Box
      alignItems={AlignItems.FlexStart}
      display={Display.Flex}
      flexDirection={FlexDirection.Column}
      gap={4}
      paddingH={4}
      paddingV={4}
      position={DisplayPosition.Relative}
      zIndex={ZIndex.Default}
    >
      <Box
        alignItems={AlignItems.FlexStart}
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        gap={2}
      >
        <Box
          alignItems={AlignItems.FlexStart}
          display={Display.Flex}
          flexDirection={FlexDirection.Column}
          gap={1}
        >
          <Scrim>
            <Body1 color={Color.White} size={2}>
              {data.welcome}
            </Body1>
          </Scrim>
          <Scrim>
            <Headline color={Color.White} size={[10, null, 13]}>
              {data.headline}
            </Headline>
          </Scrim>
        </Box>

        {data.offer ? (
          <Box
            alignItems={AlignItems.FlexStart}
            display={Display.Flex}
            flexDirection={FlexDirection.Column}
            gap={2}
          >
            <Scrim alignItems={AlignItems.Center} display={Display.Flex} gap={2}>
              <Box
                alignItems={AlignItems.Center}
                bg={Color.Success_900}
                display={Display.Flex}
                paddingH={2}
                paddingV={0.5}
                style={{ borderRadius: '4px' }}
              >
                <Text color={Color.White} fontWeight={FontWeight.Bold} size={[3, null, 5]}>
                  {data.offer.badge}
                </Text>
              </Box>
              {data.offer.label ? (
                <Body1 color={Color.White} fontWeight={FontWeight.Bold} size={[3, null, 5]}>
                  {data.offer.label}
                </Body1>
              ) : null}
            </Scrim>
            {data.offer.expiry_label ? (
              <Scrim>
                <Body1 color={Color.White} size={[1, 2]}>
                  {data.offer.expiry_label}
                </Body1>
              </Scrim>
            ) : null}
          </Box>
        ) : null}
      </Box>

      <ChecklistPill>
        {data.tags.map((tag) => (
          <ChecklistItem key={tag}>
            <Icon aria-hidden="true" name="tick" size={4} variant={IconVariant.Inherit} />
            <Text color={Color.White} data-testid="empty-state-hero-v2-tag" size={4}>
              {tag}
            </Text>
          </ChecklistItem>
        ))}
      </ChecklistPill>

      <Button
        colorVariant={ButtonColorVariant.Primary}
        data-testid="empty-state-hero-v2-cta"
        height={48}
        onClick={() => onActionClick(data.cta.intent)}
        type="button"
        width={['100%', null, 440, 440]}
      >
        {data.cta.title}
      </Button>
    </Box>
  </Container>
)
